import { useEffect, useEffectEvent, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import type { CategoryEntity } from '@actual-app/core/types/models';
import { css, keyframes } from '@emotion/css';

import { useImportTransactionsMutation } from '#accounts';
import { BocadilloGavi } from '#components/mobile/importar/BocadilloGavi';
import {
  lecturaConGuardada,
  leerFormatoGuardado,
} from '#components/mobile/importar/columnas';
import {
  aImportar,
  filasVista,
  resumen,
  seleccionImportar,
  seTrae,
} from '#components/mobile/importar/importar';
import type { FilaVista } from '#components/mobile/importar/importar';
import { leerArchivo } from '#components/mobile/importar/leerArchivo';
import type { ArchivoLeido } from '#components/mobile/importar/leerArchivo';
import type { MovimientoLeido } from '#components/mobile/importar/leerExtracto';
import { buscarParecidos } from '#components/mobile/importar/parecidos';
import { pedirVistaPrevia } from '#components/mobile/importar/vistaPrevia';
import { leerRecientes } from '#components/mobile/transactions/seleccionCategoria';
import { useAvisoSalida } from '#components/mobile/ui/avisoSalida';
import { Boton } from '#components/mobile/ui/Boton';
import {
  color,
  espacio,
  num,
  radio,
  texto,
} from '#components/mobile/ui/tokens';
import { useAccounts } from '#hooks/useAccounts';
import { useCategories } from '#hooks/useCategories';
import { useNavigate } from '#hooks/useNavigate';
import { usePayeesById } from '#hooks/usePayees';
import { usePlan } from '#hooks/usePlan';
import { useReducedMotion } from '#hooks/useReducedMotion';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { pushModal } from '#modals/modalsSlice';
import { addNotification } from '#notifications/notificationsSlice';
import { saveSyncedPrefs } from '#prefs/prefsSlice';
import { useDispatch, useStore } from '#redux';
import { undo } from '#undo';
import { registrarUso } from '#uso';

import { tomarArchivoCompartido } from './archivoCompartido';
import {
  categoriasSugeridas,
  conCategoriaElegida,
  cuentaSinPreguntar,
  dudaComercio,
  firmaArchivo,
  guardarFirma,
  leerFirmas,
  queHacer,
} from './compartido';
import type { Duda } from './compartido';
import { EscenaCorreo } from './EscenaCorreo';
import type { FaseCorreo } from './EscenaCorreo';

/** Lo que dura el vuelo de Gavi con el extracto (ms). */
const VUELO = 3400;

/** El archivo ya subido y leído, antes de saber la cuenta. */
type Llegado = { ruta: string; firma: string; crudo: ArchivoLeido };

type Leido = {
  ruta: string;
  firma: string;
  /** CSV y Excel: lo que se guarda para la próxima vez en esa cuenta. */
  filas: string[][] | null;
  lectura: Parameters<typeof lecturaConGuardada>[1];
  movimientos: MovimientoLeido[];
};

type Paso =
  | { paso: 'llega' }
  | { paso: 'decidir'; llegado: Llegado }
  | {
      paso: 'aviso';
      motivo: 'sin-archivo' | 'sin-cuenta' | 'pdf' | 'formato' | 'ilegible';
    }
  | { paso: 'cuenta'; llegado: Llegado; sugerida: string | null }
  | { paso: 'altamar'; leido: Leido; cuenta: string }
  | {
      paso: 'a-bordo';
      leido: Leido;
      cuenta: string;
      filas: FilaVista[];
      duda: Duda | null;
      sugeridas: string[];
    }
  | { paso: 'subiendo' }
  | {
      paso: 'hecho';
      cuenta: string;
      nuevos: number;
    };

const llenar = keyframes({ from: { width: '0%' }, to: { width: '100%' } });

/**
 * «Extracto en dos toques» (`/compartido`): lo que se abre al compartir un
 * extracto desde la app del banco y elegir Rumbo. Gavi llega con el archivo,
 * lo lee con el importador de siempre y solo pregunta lo que no sabe: a qué
 * cuenta va (la primera vez) y, como mucho, un comercio que no conoce. Lo
 * repetido no entra y lo del saldo inicial o de antes se queda fuera. Es de
 * Altamar: sin él, el archivo sigue paso a paso en «Importar extracto».
 */
export function CompartidoPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  const quieta = useReducedMotion();
  const { data: cuentas = [], isSuccess: cuentasListas } = useAccounts();
  const { data: comercios = {} } = usePayeesById();
  const { data: { list: categorias, grouped } = { list: [], grouped: [] } } =
    useCategories();
  const { plan, cargando: cargandoPlan, sinServidor } = usePlan();
  const [firmasRaw, setFirmasRaw] = useSyncedPref('compartir-cuentas');
  const importar = useImportTransactionsMutation();

  const [paso, setPaso] = useState<Paso>({ paso: 'llega' });
  const [elegida, setElegida] = useState<string | null>(null);
  // El archivo llega una sola vez: «atrás» a mitad pregunta antes de dejarlo.
  const { aviso: avisoSalida, salir } = useAvisoSalida(
    paso.paso === 'cuenta' ||
      paso.paso === 'altamar' ||
      paso.paso === 'a-bordo',
    {
      pregunta: t('Leave the statement without loading it?'),
      detalle: t(
        'If you leave now, you will have to share it again from your bank.',
      ),
      accion: t('Leave'),
    },
  );

  const abiertas = cuentas.filter(c => !c.closed);
  const nombreCategoria = (id: string) =>
    (categorias as CategoryEntity[]).find(c => c.id === id)?.name ?? '';
  const nombreCuenta = (id: string) =>
    cuentas.find(c => c.id === id)?.name ?? '';

  // Sin servidor no hay planes; si aún no se sabe, no se le hace esperar.
  const conAltamar =
    !sinServidor && (cargandoPlan || plan == null || plan.plan !== 'gratis');

  async function verABordo(leido: Leido, cuenta: string) {
    setPaso({ paso: 'subiendo' });
    const vista = await pedirVistaPrevia(cuenta, leido.movimientos).catch(
      () => null,
    );
    if (!vista) {
      setPaso({ paso: 'aviso', motivo: 'ilegible' });
      return;
    }
    const filas = filasVista(
      leido.movimientos,
      vista.entradas,
      vista.fechaSaldoInicial,
    );
    // Algo se parece a lo que la persona apuntó a mano: la pregunta «¿Ya lo
    // tenías?» necesita su sitio, así que se sigue en el importador.
    if (
      buscarParecidos(
        filas.filter(f => seTrae(f) && !f.antes),
        vista.apuntados,
        cuenta,
      ).length > 0
    ) {
      registrarUso('compartir', { accion: 'dudas-repetidos' });
      pasoAPaso(leido, cuenta);
      return;
    }
    const conocidos = new Set(
      Object.values(comercios).map(p => p.name.trim().toLowerCase()),
    );
    setElegida(null);
    setPaso({
      paso: 'a-bordo',
      leido,
      cuenta,
      filas,
      duda: dudaComercio(filas, conocidos),
      sugeridas: categoriasSugeridas(
        filas,
        leerRecientes().filter(id =>
          (categorias as CategoryEntity[]).some(c => c.id === id && !c.hidden),
        ),
      ),
    });
  }

  function seguir(leido: Leido, cuenta: string) {
    if (!conAltamar) {
      registrarUso('compartir', { accion: 'sin-altamar' });
      setPaso({ paso: 'altamar', leido, cuenta });
      return;
    }
    // Sin columnas reconocidas: se eligen a mano, paso a paso.
    if (leido.movimientos.length === 0) {
      pasoAPaso(leido, cuenta);
      return;
    }
    void verABordo(leido, cuenta);
  }

  function pasoAPaso(leido: Leido, cuenta: string) {
    salir(
      () =>
        void navigate(`/accounts/${cuenta}/importar`, {
          replace: true,
          state: { rutaCompartida: leido.ruta },
        }),
    );
  }

  /** Lo leído con las columnas que esa cuenta tenía guardadas. */
  function paraCuenta({ ruta, firma, crudo }: Llegado, id: string): Leido {
    if (crudo.tipo !== 'tabla') {
      return {
        ruta,
        firma,
        filas: null,
        lectura: null,
        movimientos: crudo.tipo === 'estructurado' ? crudo.movimientos : [],
      };
    }
    const { lectura, movimientos } = lecturaConGuardada(
      crudo.filas,
      crudo.lectura,
      leerFormatoGuardado(
        store.getState().prefs.synced[`importar-columnas-${id}`],
      ),
    );
    return { ruta, firma, filas: crudo.filas, lectura, movimientos };
  }

  function elegirCuenta(llegado: Llegado, cuenta: string) {
    setFirmasRaw(guardarFirma(firmasRaw, llegado.firma, cuenta));
    seguir(paraCuenta(llegado, cuenta), cuenta);
  }

  // Al llegar: se toma el archivo, se lee y se decide la cuenta mientras
  // Gavi vuela (el vuelo dura lo suyo aunque leer sea más rápido).
  const empezar = useEffectEvent(async () => {
    const inicio = Date.now();
    const esperarVuelo = () =>
      new Promise(resolve =>
        setTimeout(resolve, quieta ? 0 : VUELO - (Date.now() - inicio)),
      );
    const archivo = await tomarArchivoCompartido();
    if (!archivo) {
      await esperarVuelo();
      registrarUso('compartir', { accion: 'aviso', motivo: 'sin-archivo' });
      setPaso({ paso: 'aviso', motivo: 'sin-archivo' });
      return;
    }
    const que = queHacer(archivo.nombre);
    if (que !== 'leer') {
      await esperarVuelo();
      registrarUso('compartir', { accion: 'aviso', motivo: que });
      setPaso({ paso: 'aviso', motivo: que });
      return;
    }
    await send('upload-file-web', {
      filename: archivo.nombre,
      contents: archivo.datos,
    });
    const ruta = `/uploads/${archivo.nombre}`;
    const leido = await leerArchivo(ruta);
    await esperarVuelo();
    if (leido.tipo === 'error') {
      registrarUso('compartir', { accion: 'aviso', motivo: leido.error });
      setPaso({ paso: 'aviso', motivo: 'ilegible' });
      return;
    }
    registrarUso('compartir', { accion: 'llega', tipo: leido.tipo });
    setPaso({
      paso: 'decidir',
      llegado: {
        ruta,
        firma: firmaArchivo(archivo.nombre, leido),
        crudo: leido,
      },
    });
  });

  // ¿A qué cuenta va? En cuanto se sabe qué cuentas hay.
  const decidir = useEffectEvent((llegado: Llegado) => {
    const ids = abiertas.map(c => c.id);
    if (ids.length === 0) {
      setPaso({ paso: 'aviso', motivo: 'sin-cuenta' });
      return;
    }
    const firmas = leerFirmas(firmasRaw);
    const cuenta = cuentaSinPreguntar(firmas, llegado.firma, ids);
    if (cuenta) {
      seguir(paraCuenta(llegado, cuenta), cuenta);
      return;
    }
    registrarUso('compartir', { accion: 'pregunta-cuenta' });
    const sugerida = Object.values(firmas).find(id => ids.includes(id)) ?? null;
    setPaso({ paso: 'cuenta', llegado, sugerida });
  });

  useEffect(() => {
    if (paso.paso === 'decidir' && cuentasListas) {
      decidir(paso.llegado);
    }
  }, [paso, cuentasListas]);

  const empezado = useRef(false);
  useEffect(() => {
    if (!empezado.current) {
      empezado.current = true;
      void empezar();
    }
  }, []);

  async function subirABordo() {
    if (paso.paso !== 'a-bordo') {
      return;
    }
    const { leido, cuenta, filas, duda } = paso;
    const sinFuera = new Set<string>();
    const { nuevos } = resumen(filas, sinFuera);
    const todos = conCategoriaElegida(
      aImportar(cuenta, leido.movimientos),
      duda,
      elegida,
    );
    setPaso({ paso: 'subiendo' });
    // Antes de importar: así «Deshacer» deshace la importación y no esto.
    if (leido.filas && leido.lectura) {
      dispatchColumnas(cuenta, JSON.stringify(leido.lectura.columnas));
    }
    try {
      await importar.mutateAsync({
        accountId: cuenta,
        transactions: seleccionImportar(todos, filas, sinFuera),
        reconcile: true,
        matchOnlySameBankLine: true,
      });
    } catch {
      // La mutación ya avisa del error.
      void navigate(`/accounts/${cuenta}`, { replace: true });
      return;
    }
    registrarUso('compartir', {
      accion: 'importar',
      n: nuevos,
      resultado: elegida ? 'duda-contestada' : duda ? 'duda-luego' : 'sin-duda',
    });
    setPaso({
      paso: 'hecho',
      cuenta,
      nuevos,
    });
  }

  function dispatchColumnas(cuenta: string, valor: string) {
    // La clave depende de la cuenta: `useSyncedPref` no sirve aquí.
    void dispatch(
      saveSyncedPrefs({ prefs: { [`importar-columnas-${cuenta}`]: valor } }),
    );
  }

  function deshacer(cuenta: string) {
    undo();
    dispatch(
      addNotification({
        notification: { type: 'message', message: t('Import undone') },
      }),
    );
    void navigate(`/accounts/${cuenta}`, { replace: true });
  }

  function otraCategoria() {
    dispatch(
      pushModal({
        modal: {
          name: 'category-autocomplete',
          options: {
            categoryGroups: grouped,
            showNoneOption: false,
            month: monthUtils.currentMonth(),
            closeOnSelect: true,
            onSelect: id => setElegida(id),
          },
        },
      }),
    );
  }

  const fase: FaseCorreo =
    paso.paso === 'llega' || paso.paso === 'decidir'
      ? 'vuela'
      : paso.paso === 'hecho'
        ? 'zarpa'
        : paso.paso === 'a-bordo' || paso.paso === 'subiendo'
          ? 'carga'
          : 'posada';

  let contenido: ReactNode = null;
  switch (paso.paso) {
    case 'llega':
    case 'decidir':
    case 'subiendo':
      contenido = (
        <>
          <BocadilloGavi>
            {paso.paso !== 'subiendo'
              ? t("Got it! I'm reading the statement…")
              : t('Loading it on board…')}
          </BocadilloGavi>
          <View
            style={{
              height: 8,
              borderRadius: 4,
              backgroundColor: color.surface2,
              overflow: 'hidden',
            }}
          >
            <View
              className={
                quieta
                  ? undefined
                  : css({ animation: `${llenar} ${VUELO}ms ease forwards` })
              }
              style={{
                height: '100%',
                width: quieta ? '100%' : undefined,
                borderRadius: 4,
                backgroundColor: color.accent,
              }}
            />
          </View>
          <Text
            style={{
              ...texto.secundario,
              color: color.fg3,
              textAlign: 'center',
            }}
          >
            {t(
              'I read CSV, Excel and Norma 43, and I remember the columns of each account.',
            )}
          </Text>
        </>
      );
      break;

    case 'aviso':
      contenido = (
        <>
          <BocadilloGavi estado="ladea">
            {paso.motivo === 'pdf'
              ? t(
                  "I can't read PDFs yet: every bank lays them out its own way. In your bank's app, look for Export or Download as Excel or CSV and share that file with me.",
                )
              : paso.motivo === 'formato'
                ? t(
                    "I can't read this file. Share the statement as Excel, CSV or Norma 43.",
                  )
                : paso.motivo === 'sin-cuenta'
                  ? t(
                      'First add an account in Accounts, and then share the statement again.',
                    )
                  : paso.motivo === 'sin-archivo'
                    ? t(
                        'No file reached me. Try sharing it again, or import it by hand.',
                      )
                    : t(
                        "I couldn't read this statement. Try importing it by hand: there you can choose the columns.",
                      )}
          </BocadilloGavi>
          <View style={{ flex: 1 }} />
          {(paso.motivo === 'sin-archivo' || paso.motivo === 'ilegible') && (
            <Boton
              bloque
              onPress={() =>
                void navigate(
                  abiertas.length === 1
                    ? `/accounts/${abiertas[0].id}/importar`
                    : '/accounts',
                  { replace: true },
                )
              }
            >
              <Trans>Import by hand</Trans>
            </Boton>
          )}
          <Boton
            bloque
            variante={
              paso.motivo === 'sin-archivo' || paso.motivo === 'ilegible'
                ? 'fantasma'
                : 'primario'
            }
            onPress={() => void navigate('/', { replace: true })}
          >
            <Trans>Got it</Trans>
          </Boton>
        </>
      );
      break;

    case 'cuenta': {
      const { llegado, sugerida } = paso;
      const n =
        llegado.crudo.tipo === 'estructurado'
          ? llegado.crudo.movimientos.length
          : paraCuenta(llegado, sugerida ?? '').movimientos.length;
      contenido = (
        <>
          <BocadilloGavi>
            {n > 0
              ? t(
                  '{{count}} transactions. Which account do I load them into?',
                  {
                    count: n,
                  },
                )
              : t('Which account is this statement from?')}
          </BocadilloGavi>
          <View style={{ gap: 10 }}>
            {abiertas.map(c => (
              <Opcion
                key={c.id}
                inicial={c.name.slice(0, 1).toUpperCase()}
                titulo={c.name}
                detalle={
                  c.id === sugerida
                    ? t('The one you used last time')
                    : undefined
                }
                marcada={c.id === sugerida}
                onPress={() => elegirCuenta(llegado, c.id)}
              />
            ))}
          </View>
          <Text
            style={{
              ...texto.secundario,
              color: color.fg3,
              textAlign: 'center',
            }}
          >
            <Trans>I only ask the first time.</Trans>
          </Text>
        </>
      );
      break;
    }

    case 'altamar':
      contenido = (
        <>
          <BocadilloGavi>
            {t(
              'Loading it on board by myself is part of Altamar. I leave it for you in Import statement, step by step: nothing is lost.',
            )}
          </BocadilloGavi>
          <View style={{ flex: 1 }} />
          <Boton bloque onPress={() => pasoAPaso(paso.leido, paso.cuenta)}>
            <Trans>Continue step by step</Trans>
          </Boton>
          <Boton
            bloque
            variante="fantasma"
            onPress={() => salir(() => void navigate('/settings'))}
          >
            {t('See Altamar')}
          </Boton>
        </>
      );
      break;

    case 'a-bordo': {
      const { filas, duda, sugeridas } = paso;
      const r = resumen(filas, new Set());
      const opciones = [
        ...sugeridas,
        ...(elegida && !sugeridas.includes(elegida) ? [elegida] : []),
      ];
      contenido = (
        <>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Cifra n={r.nuevos} texto={t('new, going on board')} destacada />
            <Cifra n={r.repetidos} texto={t('already there, not repeated')} />
            <Cifra
              n={r.anteriores}
              texto={t('before your starting balance, left out')}
            />
          </View>
          {duda && (
            <>
              <BocadilloGavi estado="ladea">
                {t("Just one question: I don't know {{name}}. What is it?", {
                  name: duda.nombre,
                })}
              </BocadilloGavi>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {opciones.map(id => (
                  <Chip
                    key={id}
                    elegida={elegida === id}
                    onPress={() => setElegida(elegida === id ? null : id)}
                  >
                    {nombreCategoria(id)}
                  </Chip>
                ))}
                <Chip onPress={otraCategoria}>
                  <Trans>Other…</Trans>
                </Chip>
                <Chip luego elegida={false} onPress={() => setElegida(null)}>
                  <Trans>Later</Trans>
                </Chip>
              </View>
            </>
          )}
          {r.nuevos === 0 && (
            <BocadilloGavi>
              {t('Nothing new: everything was already on board.')}
            </BocadilloGavi>
          )}
          <View style={{ flex: 1 }} />
          {r.nuevos > 0 ? (
            <Boton
              bloque
              onPress={() => void subirABordo()}
              data-testid="subir-a-bordo"
            >
              <Trans>Load on board</Trans>
            </Boton>
          ) : (
            <Boton
              bloque
              onPress={() =>
                void navigate(`/accounts/${paso.cuenta}`, { replace: true })
              }
            >
              <Trans>See the account</Trans>
            </Boton>
          )}
        </>
      );
      break;
    }

    case 'hecho':
      contenido = (
        <>
          <Text style={{ ...texto.display, textAlign: 'center' }}>
            {t('{{count}} transactions on board!', { count: paso.nuevos })}
          </Text>
          <BocadilloGavi estado="aplaude">
            {paso.nuevos > 0
              ? t(
                  'Done. {{count}} are waiting for you in To review. I left past months untouched.',
                  { count: paso.nuevos },
                )
              : t(
                  'Done. All of them already have their category. I left past months untouched.',
                )}
          </BocadilloGavi>
          <View style={{ flex: 1 }} />
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <View style={{ flex: 1 }}>
              <Boton
                bloque
                variante="fantasma"
                onPress={() => deshacer(paso.cuenta)}
                data-testid="compartido-deshacer"
              >
                <Trans>Undo</Trans>
              </Boton>
            </View>
            <View style={{ flex: 1 }}>
              <Boton
                bloque
                onPress={() =>
                  void navigate(
                    paso.nuevos > 0 ? '/revisar' : `/accounts/${paso.cuenta}`,
                    { replace: true },
                  )
                }
              >
                {paso.nuevos > 0 ? t('Review') : t('See the account')}
              </Boton>
            </View>
          </View>
        </>
      );
      break;

    default:
      contenido = null;
  }

  const archivoCuenta =
    paso.paso === 'a-bordo' || paso.paso === 'hecho'
      ? nombreCuenta(paso.cuenta)
      : null;

  return (
    <View
      style={{
        height: '100%',
        overflow: 'hidden',
        backgroundColor: color.bg,
      }}
      data-testid="compartido"
      data-paso={paso.paso}
    >
      <EscenaCorreo fase={fase} />
      <View
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          marginTop: -24,
          borderRadius: `${radio.heroe}px ${radio.heroe}px 0 0`,
          backgroundColor: color.bg,
          padding: `20px ${espacio.margen}px calc(${espacio.margen}px + env(safe-area-inset-bottom))`,
          gap: 14,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <Text style={{ ...texto.display, fontSize: 22 }}>
            <Trans>Mail from the bank</Trans>
          </Text>
          {archivoCuenta && (
            <Text
              style={{
                ...texto.secundario,
                color: color.fg3,
                fontWeight: 700,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {archivoCuenta}
            </Text>
          )}
        </View>
        {contenido}
      </View>
      {avisoSalida}
    </View>
  );
}

function Cifra({
  n,
  texto: etiqueta,
  destacada = false,
}: {
  n: number;
  texto: string;
  destacada?: boolean;
}) {
  return (
    <View
      style={{
        flex: 1,
        backgroundColor: color.surface,
        borderRadius: radio.sm + 2,
        padding: '10px 8px',
        alignItems: 'center',
      }}
    >
      <Text
        style={{
          ...texto.cifra,
          ...num,
          fontSize: 24,
          color: destacada ? color.accent : color.fg,
        }}
      >
        {n}
      </Text>
      <Text
        style={{
          ...texto.secundario,
          fontSize: 11.5,
          color: color.fg3,
          textAlign: 'center',
        }}
      >
        {etiqueta}
      </Text>
    </View>
  );
}

function Chip({
  children,
  elegida = false,
  luego = false,
  onPress,
}: {
  children: ReactNode;
  elegida?: boolean;
  luego?: boolean;
  onPress: () => void;
}) {
  return (
    <Button
      variant="bare"
      onPress={onPress}
      aria-pressed={elegida}
      style={{
        minHeight: 44,
        padding: '0 16px',
        borderRadius: radio.pildora,
        fontWeight: 600,
        fontSize: 14,
        border: `1.5px solid ${elegida ? color.accent : color.line}`,
        backgroundColor: elegida
          ? color.accent
          : luego
            ? 'transparent'
            : color.surface,
        color: elegida ? color.accentInk : luego ? color.fg2 : color.fg,
      }}
    >
      {children}
    </Button>
  );
}

function Opcion({
  inicial,
  titulo,
  detalle,
  marcada,
  onPress,
}: {
  inicial: string;
  titulo: string;
  detalle?: string;
  marcada: boolean;
  onPress: () => void;
}) {
  return (
    <Button
      variant="bare"
      onPress={onPress}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 12,
        minHeight: 60,
        padding: '10px 14px',
        borderRadius: radio.tarjeta - 4,
        backgroundColor: color.surface,
        border: `1.5px solid ${marcada ? color.accent : 'transparent'}`,
        textAlign: 'left',
      }}
    >
      <View
        style={{
          width: 36,
          height: 36,
          borderRadius: radio.sm,
          backgroundColor: color.accentSoft,
          color: color.accent,
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
        }}
      >
        {inicial}
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ ...texto.fila, color: color.fg }}>{titulo}</Text>
        {detalle && (
          <Text style={{ ...texto.secundario, color: color.fg3, fontSize: 12 }}>
            {detalle}
          </Text>
        )}
      </View>
      <View
        style={{
          width: 22,
          height: 22,
          borderRadius: 11,
          border: marcada
            ? `7px solid ${color.accent}`
            : `2px solid ${color.line2}`,
        }}
      />
    </Button>
  );
}
